import { FC, MouseEvent, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { isBloggerBusiness } from '../../../../utils/accessService';
import { buildAvatarImageSource } from '../../../../utils/buildAvatarImageSource';
import { cropPublicId } from '../../../../utils/cropPublicId';
import { IconFollow } from '../../../icon/IconFollow';
import { IconLink } from '../../../icon/IconLink';
import { IconUnfollowChecked } from '../../../icon/IconUnfollowChecked';
import { IconButton } from '../../../input/IconButton';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { Flex } from '../../../layout/Flex';
import { Avatar } from '../../../media/Avatar';
import { Typography } from '../../../typography/Typography';
import { Text } from '../../PostCard/AnimateFooter/styles';
import { PostUI } from '../../types';
import { PostDetailsCardProps } from '../PostDetailsCard';

export interface PostProfileProps {
    post: PostUI;
    index: PostDetailsCardProps['index'];
    onSubscribe?: PostDetailsCardProps['onSubscribe'];
}

export const PostProfile: FC<PostProfileProps> = ({ post, index, onSubscribe }) => {
    const handleSubscribe = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            onSubscribe?.({ postNumber: index });
            event.stopPropagation();
        },
        [index, onSubscribe],
    );

    const businessName = isBloggerBusiness(post.business?.type)
        ? post.business?.displayName
        : cropPublicId(post.business?.publicId || '');

    return (
        <Box overflow="hidden">
            <Flex
                pointerEvents="auto"
                width="100%"
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
            >
                {post.business ? (
                    <Flex direction="row" alignItems="center" width="100%">
                        <Flex
                            as={Link}
                            to={post.business.url}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            textDecoration="none"
                            dataTestId={DataTestId.BUSINESS_LOGIN}
                            overflow="hidden"
                        >
                            <Avatar
                                width={34}
                                minWidth={26}
                                title={post.business.displayName}
                                src={post.business.avatarUrl}
                                border="1px solid var(--divider)"
                            />
                            <Text weight="bold">{businessName}</Text>
                        </Flex>
                        <Box ml={1}>
                            <IconButton
                                onClick={handleSubscribe}
                                marginBottom={2}
                                dataTestId={DataTestId.SUBSCRIBE_ICON}
                            >
                                {post.business?.subscribed ? (
                                    <IconUnfollowChecked
                                        fontSize={24}
                                        color="#FFFFFF"
                                        dataTestId={DataTestId.SUBSCRIBED}
                                    />
                                ) : (
                                    <IconFollow
                                        fontSize={24}
                                        color="#FFFFFF"
                                        dataTestId={DataTestId.UNSUBSCRIBED}
                                    />
                                )}
                            </IconButton>
                        </Box>
                    </Flex>
                ) : null}
                {post.ownersData ? (
                    <Flex direction="row" alignItems="center" width="100%">
                        <Flex
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            textDecoration="none"
                            dataTestId={DataTestId.USER_LOGIN}
                        >
                            <Flex position="relative">
                                <Avatar
                                    width={24}
                                    title={post.ownersData.author.displayName}
                                    src={buildAvatarImageSource(post.ownersData.author.avatarUrl)}
                                />
                                <Box
                                    position="absolute"
                                    right="-3px"
                                    top="-3px"
                                    borderRadius="50%"
                                    backgroundColor="#FFF"
                                    border="solid 2px transparent"
                                >
                                    <IconLink size={10} />
                                </Box>
                            </Flex>
                            <Flex>
                                <Typography variant="caption" weight="bold" color="#FFF">
                                    {post.ownersData.shop.displayUrl}
                                </Typography>
                                <Typography variant="caption2" color="#FFF">
                                    {post.ownersData.author.displayName}
                                </Typography>
                            </Flex>
                        </Flex>
                    </Flex>
                ) : null}
            </Flex>
        </Box>
    );
};
